// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import { useSession } from '../context/SessionContext';
import { formatMemberDisplayName } from '../components/MemberName';

const COUNTRIES = [
  { value: 'IE', label: 'Ireland' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'US', label: 'United States' },
  { value: 'FR', label: 'France' },
  { value: 'DE', label: 'Germany' },
  { value: 'ES', label: 'Spain' },
  { value: 'IT', label: 'Italy' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'BE', label: 'Belgium' },
  { value: 'AT', label: 'Austria' },
  { value: 'CH', label: 'Switzerland' },
  { value: 'PT', label: 'Portugal' },
  { value: 'SE', label: 'Sweden' },
  { value: 'NO', label: 'Norway' },
  { value: 'DK', label: 'Denmark' },
  { value: 'FI', label: 'Finland' },
];

const CURRENCIES = [
  { value: 'EUR', label: 'Euro (€)' },
  { value: 'GBP', label: 'British Pound (£)' },
  { value: 'USD', label: 'US Dollar ($)' },
  { value: 'CHF', label: 'Swiss Franc (CHF)' },
  { value: 'SEK', label: 'Swedish Krona (SEK)' },
  { value: 'NOK', label: 'Norwegian Krone (NOK)' },
  { value: 'DKK', label: 'Danish Krone (DKK)' },
];

export default function RegisterClub() {
  const { setSession } = useSession();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    // Club information
    clubName: '',
    clubDescription: '',
    country: 'IE',
    currency: 'EUR',
    primaryContactName: '',
    primaryContactEmail: '',
    referralCode: searchParams.get('ref') || '',

    // Admin account
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    adminPassword: '',
    confirmPassword: '',
    adminPhone: '',

    // UX
    useContactEmailForAdmin: true,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Sync admin email with contact email if toggle is on
  useEffect(() => {
    if (formData.useContactEmailForAdmin) {
      setFormData((prev) => ({ ...prev, adminEmail: prev.primaryContactEmail }));
    }
  }, [formData.primaryContactEmail, formData.useContactEmailForAdmin]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (
      !formData.clubName.trim() ||
      !formData.country ||
      !formData.currency ||
      !formData.primaryContactName.trim() ||
      !formData.primaryContactEmail.trim() ||
      !formData.adminFirstName.trim() ||
      !formData.adminLastName.trim() ||
      !formData.adminEmail.trim() ||
      !formData.adminPassword
    ) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.adminPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.adminPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!isValidEmail(formData.adminEmail) || !isValidEmail(formData.primaryContactEmail)) {
      setError('Please enter valid email addresses');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/auth/club/register', {
        club: {
          name: formData.clubName.trim(),
          description: formData.clubDescription.trim() || undefined,
          country: formData.country,
          default_currency: formData.currency,
          primary_contact_name: formData.primaryContactName.trim(),
          primary_contact_email: formData.primaryContactEmail.trim(),
        },
        admin: {
          first_name: formData.adminFirstName.trim(),
          last_name: formData.adminLastName.trim(),
          email: formData.adminEmail.trim(),
          password: formData.adminPassword,
          phone: formData.adminPhone.trim() || undefined,
        },
        referral_code: formData.referralCode.trim() || undefined,
      });

      const { token, member, club: registeredClub, club_status } = response.data;

      // N31: If club is pending approval, redirect to pending page (no token issued)
      if (club_status === 'pending' || !token) {
        navigate('/club-pending-approval', {
          state: { clubName: registeredClub?.name },
        });
        return;
      }

      // Store token and session
      localStorage.setItem('clubview_token', token);
      localStorage.removeItem('clubview_club_token');

      setSession({
        // Self-view username — admin role bypasses child masking (only adults
        // can register a club).
        username: formatMemberDisplayName({ first_name: member.first_name ?? '', last_name: member.last_name ?? '' }, 'admin') || member.email || 'admin',
        role: 'admin',
        club: { id: member.club_id, name: registeredClub.name },
      });

      // Redirect to dashboard with welcome message
      navigate('/dashboard', {
        state: { welcome: true, clubName: registeredClub.name },
      });
    } catch (err: unknown) {
      let errorMessage = 'Unable to create club. Please try again.';

      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as {
          response?: { data?: { message?: string }; status?: number };
        };
        const status = axiosError.response?.status;

        if (status === 400) {
          errorMessage =
            axiosError.response?.data?.message || 'Invalid input. Please check your information.';
        } else if (status === 409) {
          errorMessage =
            'This email is already registered as an admin of another club. Please use a different email or sign in instead.';
        } else if (status === 403) {
          errorMessage = 'Club registration is not available. Please contact support.';
        } else if (status === 429) {
          errorMessage = 'Too many registration attempts. Please try again later.';
        } else if (axiosError.response?.data?.message) {
          errorMessage = axiosError.response.data.message;
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const isValidEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-brand-50 via-white to-white px-4 py-10">
      <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-md">
        <h1 className="mb-2 text-center text-2xl font-semibold text-brand-700">
          Start Your Club on ClubView
        </h1>
        <p className="mb-6 text-center text-sm text-slate-600">
          Create your club and admin account to get started
        </p>

        <form className="space-y-6" onSubmit={handleSubmit} noValidate>
          {/* Club Information Section */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-900">Club Information</h2>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="clubName">
                Club Name *
              </label>
              <input
                id="clubName"
                type="text"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
                placeholder="Dublin GAA Club"
                value={formData.clubName}
                onChange={(e) => setFormData({ ...formData, clubName: e.target.value })}
                required
              />
            </div>

            <div>
              <label
                className="mb-2 block text-sm font-medium text-slate-700"
                htmlFor="clubDescription"
              >
                Description (Optional)
              </label>
              <textarea
                id="clubDescription"
                rows={3}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
                placeholder="Brief description of your club..."
                value={formData.clubDescription}
                onChange={(e) => setFormData({ ...formData, clubDescription: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="country">
                  Country *
                </label>
                <select
                  id="country"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  required
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  className="mb-2 block text-sm font-medium text-slate-700"
                  htmlFor="currency"
                >
                  Currency *
                </label>
                <select
                  id="currency"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  required
                >
                  {CURRENCIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label
                className="mb-2 block text-sm font-medium text-slate-700"
                htmlFor="primaryContactName"
              >
                Primary Contact Name *
              </label>
              <input
                id="primaryContactName"
                type="text"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
                placeholder="John Doe"
                value={formData.primaryContactName}
                onChange={(e) =>
                  setFormData({ ...formData, primaryContactName: e.target.value })
                }
                required
              />
            </div>

            <div>
              <label
                className="mb-2 block text-sm font-medium text-slate-700"
                htmlFor="primaryContactEmail"
              >
                Primary Contact Email *
              </label>
              <input
                id="primaryContactEmail"
                type="email"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
                placeholder="contact@club.example.com"
                value={formData.primaryContactEmail}
                onChange={(e) =>
                  setFormData({ ...formData, primaryContactEmail: e.target.value })
                }
                autoComplete="email"
                required
              />
            </div>
          </div>

          {/* Admin Account Section */}
          <div className="space-y-4 border-t border-slate-200 pt-6">
            <h2 className="text-lg font-semibold text-slate-900">Admin Account</h2>
            <p className="text-sm text-slate-600">
              Create your administrator account to manage your club
            </p>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="adminFirstName">
                  First Name *
                </label>
                <input
                  id="adminFirstName"
                  type="text"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
                  placeholder="John"
                  value={formData.adminFirstName}
                  onChange={(e) => setFormData({ ...formData, adminFirstName: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="adminLastName">
                  Last Name *
                </label>
                <input
                  id="adminLastName"
                  type="text"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
                  placeholder="Doe"
                  value={formData.adminLastName}
                  onChange={(e) => setFormData({ ...formData, adminLastName: e.target.value })}
                  required
                />
              </div>
            </div>

            <div>
              <label
                className="mb-2 block text-sm font-medium text-slate-700"
                htmlFor="adminEmail"
              >
                Email *
              </label>
              <input
                id="adminEmail"
                type="email"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
                placeholder="admin@club.example.com"
                value={formData.adminEmail}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    adminEmail: e.target.value,
                    useContactEmailForAdmin: false,
                  })
                }
                autoComplete="email"
                required
              />
              <div className="mt-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.useContactEmailForAdmin}
                    onChange={(e) =>
                      setFormData({ ...formData, useContactEmailForAdmin: e.target.checked })
                    }
                    className="rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  />
                  <span className="text-xs text-slate-600">
                    Use primary contact email for admin account
                  </span>
                </label>
              </div>
            </div>

            <div>
              <label
                className="mb-2 block text-sm font-medium text-slate-700"
                htmlFor="adminPassword"
              >
                Password *
              </label>
              <input
                id="adminPassword"
                type="password"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
                placeholder="••••••••"
                value={formData.adminPassword}
                onChange={(e) => setFormData({ ...formData, adminPassword: e.target.value })}
                autoComplete="new-password"
                required
                minLength={8}
              />
              <p className="mt-1 text-xs text-slate-500">Must be at least 8 characters</p>
            </div>

            <div>
              <label
                className="mb-2 block text-sm font-medium text-slate-700"
                htmlFor="confirmPassword"
              >
                Confirm Password *
              </label>
              <input
                id="confirmPassword"
                type="password"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
                placeholder="••••••••"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                autoComplete="new-password"
                required
              />
            </div>

            <div>
              <label
                className="mb-2 block text-sm font-medium text-slate-700"
                htmlFor="adminPhone"
              >
                Phone (Optional)
              </label>
              <input
                id="adminPhone"
                type="tel"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
                placeholder="+353 1 234 5678"
                value={formData.adminPhone}
                onChange={(e) => setFormData({ ...formData, adminPhone: e.target.value })}
                autoComplete="tel"
              />
            </div>
          </div>

          {/* Referral Code (optional) */}
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="referralCode">
              Referral Code (optional)
            </label>
            <input
              id="referralCode"
              type="text"
              value={formData.referralCode}
              onChange={(e) => setFormData((prev) => ({ ...prev, referralCode: e.target.value }))}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-200"
              placeholder="Enter referral code if you have one"
              data-testid="referral-code-input"
            />
            <p className="mt-1 text-xs text-slate-500">
              If another club referred you, enter their code for mutual benefits
            </p>
          </div>

          {error && (
            <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          )}

          <button
            type="submit"
            className="w-full rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-200 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? 'Creating club...' : 'Create Club & Account'}
          </button>

          <div className="space-y-2 text-center text-sm">
            <div>
              <span className="text-slate-600">Already have an account? </span>
              <Link to="/login" className="text-brand-600 hover:text-brand-700">
                Sign in
              </Link>
            </div>
            <div>
              <span className="text-slate-600">Joining an existing club? </span>
              <Link to="/register" className="text-brand-600 hover:text-brand-700">
                Sign up as member
              </Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
